import { logger } from "./logger";
// ========================================
// 🎨 ENHANCED TEXT CONTRAST SYSTEM
// Complete solution for automatic text contrast
// ========================================
// Add this at the very top of textContrast.js
/**
 * Enhanced brightness calculation with better color format support
 */
export const getColorBrightness = (color) => {
  if (!color || color === 'transparent') return 255;
  
  let r, g, b;
  
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    }
  }
  // Handle rgb/rgba colors
  else if (color.includes('rgb')) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    }
  }
  // Handle CSS color names
  else {
    const colorMap = {
      'white': [255, 255, 255],
      'black': [0, 0, 0],
      'red': [255, 0, 0],
      'green': [0, 128, 0],
      'blue': [0, 0, 255],
      'transparent': [255, 255, 255]
    };
    const mapped = colorMap[color.toLowerCase()];
    if (mapped) {
      [r, g, b] = mapped;
    } else {
      return 128; // Default to medium brightness
    }
  }
  
  // Calculate relative luminance using WCAG formula
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness;
};

/**
 * Get pure black or white text color based on background
 */
export const getContrastTextValue = (backgroundColor) => {
  const brightness = getColorBrightness(backgroundColor);
  return brightness < 128 ? '#ffffff' : '#000000';
};

/**
 * 🎯 MAIN CONTRAST UPDATE FUNCTION
 * This is the core function that updates all CSS variables
 */
export const updateContrastUtilityClasses = () => {
  const root = document.documentElement;
  
  // Get current team colors
  const primaryColor = getComputedStyle(root).getPropertyValue('--sbe-primary') || '#24777c';
  const secondaryColor = getComputedStyle(root).getPropertyValue('--sbe-secondary') || '#10b981';
  
  // Calculate optimal text colors
  const primaryBrightness = getColorBrightness(primaryColor.trim());
  const primaryTextColor = primaryBrightness < 128 ? '#ffffff' : '#000000';
  
  const secondaryBrightness = getColorBrightness(secondaryColor.trim());
  const secondaryTextColor = secondaryBrightness < 128 ? '#ffffff' : '#000000';
  
  // Update CSS custom properties
  root.style.setProperty('--sbe-text-adaptive', primaryTextColor);
  root.style.setProperty('--sbe-text-on-primary', primaryTextColor === '#000000' ? '#ffffff' : '#000000');
  root.style.setProperty('--sbe-text-on-secondary', secondaryTextColor);
  
  logger.debug(` Contrast updated: ${primaryColor} (${primaryBrightness}) → ${primaryTextColor}`);
  logger.debug(` Secondary: ${secondaryColor} (${secondaryBrightness}) → ${secondaryTextColor}`);
};

/**
 * Apply adaptive text classes to game elements
 */
export const applyAdaptiveTextClasses = () => {
  // Target main text elements
  const textSelectors = [
    'h1, h2, h3, h4, h5, h6',
    'p, span:not(.sbe-debug *)',
    'label, .sbe-field-label',
    '.sbe-step-title, .sbe-main-heading',
    '.sbe-step-description',
    '.sbe-grade-label, .sbe-confidence-label',
    '.sbe-team-name, .sbe-player-name'
  ];
  
  textSelectors.forEach(selector => {
    try {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Add adaptive text class instead of setting inline styles
        if (!element.classList.contains('sbe-debug') && 
            !element.closest('.sbe-debug')) {
          element.classList.add('sbe-text-adaptive');
        }
      });
    } catch (e) {
      console.warn('Could not apply adaptive class to selector:', selector);
    }
  });
};

/**
 * Set up observers for theme changes
 */
export const setupThemeObserver = () => {
  // Watch for CSS variable changes
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        const root = document.documentElement;
        const currentPrimary = getComputedStyle(root).getPropertyValue('--sbe-primary');
        if (currentPrimary) {
          // Debounce updates
          clearTimeout(window.contrastUpdateTimeout);
          window.contrastUpdateTimeout = setTimeout(() => {
            updateContrastUtilityClasses();
          }, 100);
        }
      }
    });
  });
  
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style']
  });
  
  // Watch for team theming class changes
  const bodyObserver = new MutationObserver(() => {
    if (document.body.classList.contains('sbe-team-themed')) {
      setTimeout(() => {
        updateContrastUtilityClasses();
        applyAdaptiveTextClasses();
      }, 150); // Allow CSS to apply
    }
  });
  
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class']
  });
};

/**
 * Initialize the unified contrast system
 */
export const initializeUnifiedContrastSystem = () => {
  console.log('🎨 Initializing unified text contrast system...');
  
  // Set up initial CSS properties
  updateContrastUtilityClasses();
  
  // Create observer for theme changes
  setupThemeObserver();
  
  // Apply adaptive text classes
  applyAdaptiveTextClasses();
  
  logger.success('✅ Text contrast system ready');
};

/**
 * Force update all text contrast (use after theme changes)
 */
export const forceUpdateAllContrast = () => {
  logger.component('🔄 Force updating all text contrast...');
  
  updateContrastUtilityClasses();
  applyAdaptiveTextClasses();
  
  logger.success('✅ Contrast update complete');
};

/**
 * Utility function for testing contrast
 */
export const testContrastSystem = () => {
  console.log('🧪 Testing contrast system...');
  
  const root = document.documentElement;
  const primaryColor = getComputedStyle(root).getPropertyValue('--sbe-primary');
  const adaptiveColor = getComputedStyle(root).getPropertyValue('--sbe-text-adaptive');
  
  console.log(`Primary color: ${primaryColor}`);
  console.log(`Adaptive text: ${adaptiveColor}`);
  console.log(`Brightness: ${getColorBrightness(primaryColor)}`);
  console.log(`Recommended text: ${getContrastTextValue(primaryColor)}`);
  
  // Count elements using adaptive classes
  const adaptiveElements = document.querySelectorAll('.sbe-text-adaptive');
  console.log(`Elements with adaptive text: ${adaptiveElements.length}`);
};

/**
 * Debug and development tools
 */
export const enableContrastDebugging = () => {
  // Add visual indicators for contrast testing
  const style = document.createElement('style');
  style.textContent = `
    .sbe-contrast-debug {
      outline: 2px dashed red !important;
      position: relative;
    }
    
    .sbe-contrast-debug::after {
      content: attr(data-contrast-info);
      position: absolute;
      top: -20px;
      left: 0;
      background: red;
      color: white;
      padding: 2px 4px;
      font-size: 10px;
      z-index: 10000;
    }
  `;
  document.head.appendChild(style);
  
  // Add contrast info to elements
  document.querySelectorAll('.sbe-text-adaptive').forEach(element => {
    const bg = detectElementBackground(element);
    const brightness = getColorBrightness(bg);
    element.classList.add('sbe-contrast-debug');
    element.setAttribute('data-contrast-info', `B:${Math.round(brightness)}`);
  });
  
  logger.debug('🐛 Contrast debugging enabled');
};

/**
 * Detect background color from element hierarchy
 */
export const detectElementBackground = (element) => {
  if (!element) return '#ffffff';
  
  let currentElement = element;
  
  while (currentElement && currentElement !== document.body) {
    const computedStyle = window.getComputedStyle(currentElement);
    const backgroundColor = computedStyle.backgroundColor;
    const backgroundImage = computedStyle.backgroundImage;
    
    // Check for solid background color
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
    
    // Check for gradient background
    if (backgroundImage && backgroundImage !== 'none' && backgroundImage.includes('gradient')) {
      return backgroundImage;
    }
    
    currentElement = currentElement.parentElement;
  }
  
  // Check team theming
  const rootStyle = getComputedStyle(document.documentElement);
  const primaryColor = rootStyle.getPropertyValue('--sbe-primary');
  if (primaryColor && document.body.classList.contains('sbe-team-themed')) {
    return primaryColor.trim();
  }
  
  return '#ffffff';
};

// Auto-initialize when loaded
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeUnifiedContrastSystem();
    });
  } else {
    initializeUnifiedContrastSystem();
  }
  
  // Expose functions to global scope for debugging
  window.sbeContrast = {
    test: testContrastSystem,
    update: forceUpdateAllContrast,
    debug: enableContrastDebugging,
    init: initializeUnifiedContrastSystem
  };
  
  console.log('🎨 Enhanced text contrast system fully loaded');
}